import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { ApolloProvider } from 'lib/core/components/ApolloProvider'
import { JsonLd } from 'lib/shared'
import { useCategoryByPath, useProductByPath } from 'lib/modules/catalog'
import { useStoreConfig } from 'lib/core/hooks'
import { getUrlPath } from 'lib/core/resources/helpers'
import { getPageList } from 'lib/core/data/common/pageList/selectors'

interface Props {
  type?: 'PLP' | 'PDP' | 'CMS'
  title?: string
  description?: string
  keywords?: string
  extra?: [{name: string, content: string}]
  includeSuffix?: boolean
}

export const MetaData = (props: Props) => {
  return (
    <ApolloProvider>
      <MetaDataComponent {...props} />
    </ApolloProvider>
  )
}

const MetaDataComponent = ({ type, title, description, keywords, extra, includeSuffix = true }: Props) => {
  const { pwaUrl } = useStoreConfig()
  const { meta: categoryMetaData } = useCategoryByPath()
  const { product } = useProductByPath()
  const location = useLocation()
  const slug = getUrlPath(location)
  const pageList = useSelector(getPageList)
  const cmsPageData = pageList?.identifier[slug]
  const { headerLogo, defaultMetaData } = useStoreConfig()
  const history = useHistory()

  let metaDescription
  let metaKeywords
  let image = headerLogo
  let pageTitle

  switch (type) {
    case 'PLP':
      metaDescription = categoryMetaData?.description
      metaKeywords = categoryMetaData?.keywords
      pageTitle = title ?? categoryMetaData?.title
      break
    case 'PDP':
      metaDescription = product?.metaDescription
      metaKeywords = product?.metaKeyword
      image = product?.image.default.url ?? ''
      pageTitle = title ?? (product?.metaTitle ? product.metaTitle : product?.name)
      break
    case 'CMS':
      metaDescription = cmsPageData?.meta_description
      metaKeywords = cmsPageData?.meta_keywords
      pageTitle = title ?? cmsPageData?.meta_title ? cmsPageData.meta_title : cmsPageData?.title
      break
    default:
      metaDescription = defaultMetaData?.metaDescription
      metaKeywords = defaultMetaData?.metaKeywords
      pageTitle = title ?? defaultMetaData?.metaTitle
  }

  if (description) {
    metaDescription = description
  }

  if (keywords) {
    metaKeywords = keywords
  }

  const productPath = product?.canonicalUrl ?? product?.urlKey
  const canonicalUrl = !pwaUrl ? undefined : productPath
    ? `${pwaUrl}/${productPath}`
    : `${pwaUrl}${history.location.pathname}`

  return (
    <>
      <Helmet
        titleTemplate={includeSuffix ? `%s — ${process.env.REACT_APP_TITLE}` : '%s'}
      >
        {pageTitle && (
          <title>
            {pageTitle}
          </title>
        )}
        {metaKeywords && (
          <meta name='keywords' content={metaKeywords} />
        )}
        {metaDescription && (
          <meta name='description' content={metaDescription} />
        )}
        {image && (
          <meta property='og:image' content={image} />
        )}
        {extra?.map((metaData) => {
          return (
            <meta key={metaData.name} name={metaData.name} content={metaData.content} />
          )
        })}
        {canonicalUrl && (
          <link rel='canonical' href={canonicalUrl} />
        )}
      </Helmet>

      {product && (
        <JsonLd.JsonLdProduct product={product} />
      )}
    </>
  )
}
